import React, { useCallback } from 'react'
import './order-table.scss'
import $msg from '@/utils/message'
import { useRequest } from 'ahooks'
import { IMallOrder } from '../../type'
import { IPagination } from '@/model/types/common'
import { $post } from '@/api'
import { ColumnsType } from 'antd/lib/table'
import { Row, Column } from '@/components/layout/layout'
import useBemClass from '@/utils/hooks/useBEMClass'
import { AutoResizer, WithScrollLoad } from '@/utils/utils-widget'
import { Table } from 'antd'
import Ms from './image/miaosha.svg'
import Jl from './image/jielong.svg'
import Pt from './image/pintuan.svg'
import Mh from './image/manghe.svg'
import Kj from './image/kanjia.svg'
import { get } from '@/utils'
import Iconfont from '@/components/iconfont/iconfont'
import { showLayer } from '@/utils/layer'
import MallOrderDetail from '../order-detail/order-detail'
import { mallOrderFilterStore } from '../../order-filter/filter-store'
import { observer } from 'mobx-react'
import newUserImg from '@/assets/images/new-user.svg'
import { mallOrderStore } from '../../store'
import WordHighLight from '@/components/word-highlight/word-highlight'

export const mallOrderTypeTag: Record<string, string> = {
    '0': Jl,
    '2': Kj,
    '3': Pt,
    '4': Mh,
    '5': Ms,
}

interface MallOrderTableProps {}

export const useMallTableColumn = () => {
    const cls = useBemClass('mall-order-table')

    const userInfo = (user: IMallOrder.Customer, showTag: boolean) => (
        <Row data-align-items-center>
            <div data-position-relative>
                <img
                    style={{
                        border: `1px solid ${user.gender === 2 ? '#ee5b5b' : '#3c90ff'}`,
                    }}
                    className={cls('avatar')}
                    src={user.avatar}
                    alt=""
                />

                {showTag && <img className={cls('avatar', 'tag')} src={newUserImg} alt="" />}
            </div>
            <WordHighLight
                classes={cls('nickname')}
                keyword={mallOrderFilterStore.keyWord}
                word={user.nickname}
            ></WordHighLight>
        </Row>
    )

    const getGoodName = useCallback((name: string[]) => {
        if (name.length > 3) {
            return `${name.slice(0, 3).join('、')}...等${name.length}种`
        } else {
            return name.join('、')
        }
    }, [])

    const columns: ColumnsType<IMallOrder.OnlineOrder> = [
        {
            title: '#',
            key: 'index',
            align: 'center',
            width: 40,
            render: (value, record, index) => index + 1,
        },
        {
            title: '下单时间',
            width: 110,
            render: (v, r) => r.order_info.create_time,
        },
        {
            title: '顾客昵称',
            width: 160,
            render: (v, r) => userInfo(r.order_info.customer, r.order_info.first_order),
        },
        {
            title: '订单类型',
            width: 120,
            render: (v, r) => {
                const src = get(mallOrderTypeTag, r.has_seckill_goods ? '5' : `${r.order_info.order_type}`, '')

                return <Row data-align-items-center>{src ? <img src={src} alt="" /> : '-'}</Row>
            },
        },
        {
            title: '配送类型',
            width: 305,
            render: (v, r) => {
                return r.order_info.delivery_type === 0 ? (
                    <Row data-align-items-center>
                        <Iconfont name="icon13ziti" size={20} />
                        <span data-color-000 data-font-weight-600 data-margin-left-4>
                            自提
                        </span>
                    </Row>
                ) : (
                    <Column data-padding-v-8>
                        <Row data-align-items-center>
                            <Iconfont name="icon13peisong" size={20} />
                            <span data-color-000 data-font-weight-600 data-margin-left-4>
                                配送
                            </span>
                        </Row>
                        <span className={cls('address')}>{r.order_info.address}</span>
                    </Column>
                )
            },
        },
        {
            title: '商品信息',
            render: (v, r) => <div className={cls('good-name')}>{getGoodName(r.order_goods_names)}</div>,
        },
        {
            title: '实付',
            dataIndex: 'subtotal',
            width: 140,
            align: 'right',
            render: (v, r) => `¥${+r.order_info.actual_payment}`,
        },
        {
            title: '操作',
            key: 'is_draft',
            width: 74,
            align: 'center',
            render: (val, record) => (
                <div
                    className="action"
                    onClick={() => {
                        showLayer(
                            <MallOrderDetail
                                orderId={record.order_info.order_id}
                                orderType={record.order_info.order_type}
                            />,
                        )
                    }}
                >
                    详情
                </div>
            ),
        },
    ]

    return columns
}

const MallOrderTable: React.FC<MallOrderTableProps> = props => {
    const cls = useBemClass('mall-order-table')
    const columns = useMallTableColumn()

    const requestHandle = useRequest<
        {
            list: IMallOrder.OnlineOrder[]
            pagination: IPagination
        },
        {
            orders: IMallOrder.OnlineOrder[]
            pagination: IPagination
        }
    >(
        val => {
            const status = (mallOrderFilterStore.filterQuery.status_list || [3]).filter(s => [3, 6].includes(s))

            return status.length
                ? $post({
                      url: '/mall/getshoponlineorders',
                      params: {
                          limit: 20,
                          page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                          from_date: mallOrderFilterStore.date.length ? mallOrderFilterStore.date[0] : void 0,
                          to_date: mallOrderFilterStore.date.length ? mallOrderFilterStore.date[1] : void 0,
                          keyword: mallOrderFilterStore.keyWord || void 0,
                          ...mallOrderFilterStore.filterQuery,
                          status_list: status.length ? status : [11],
                      },
                  })
                : new Promise(resolve => {
                      resolve({
                          can_bulk_order_nos: [],
                          orders: [],
                          pagination: { page: 1, page_count: 1, limit: 20 },
                      })
                  })
        },
        {
            refreshDeps: [
                mallOrderFilterStore.filterMap,
                mallOrderFilterStore.date,
                mallOrderFilterStore.keyWord,
                mallOrderStore.orderListRefresh,
            ],
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            formatResult: data => {
                return {
                    list: data.orders,
                    pagination: data.pagination,
                }
            },
            onError: e => {
                $msg('error', e.message)
            },
        },
    )

    return (
        <Column data-expaned data-overflow-hidden>
            <div className={cls('title')}>已完成</div>
            <div className="table-container" data-position-relative>
                <AutoResizer>
                    {({ height }) => {
                        return (
                            <WithScrollLoad data-expaned data-overflow-hidden scrollloadhandle={requestHandle}>
                                <Table
                                    rowKey={record => record.order_info.order_no}
                                    loading={requestHandle.loading || requestHandle.loadingMore || void 0}
                                    bordered
                                    pagination={false}
                                    columns={columns}
                                    dataSource={requestHandle.data?.list}
                                    scroll={{ x: 'max-content', y: height - 50 }}
                                />
                            </WithScrollLoad>
                        )
                    }}
                </AutoResizer>
            </div>
        </Column>
    )
}

export default observer(MallOrderTable)
